/**
 * A condição montada por escolhas: o campo, "é" ou "não é", e o valor, todos
 * escolhidos das listas que o CRM manda. Ninguém escreve `= 'contact'`.
 *
 * - Cada linha é uma regra; as linhas valem juntas ("e").
 * - "Escrever a expressão" abre o texto, para o que as escolhas não cobrem.
 *   A expressão que não cabe nas escolhas abre direto no texto, como está.
 * - Campo cuja lista a pessoa não pode ver (a equipe, para quem não
 *   administra) pede o valor escrito, só naquela linha.
 */

import { useId, useState, type ReactNode } from 'react';
import type { CampoDoEvento } from '@cliqcrm/contracts';
import { Botao, Campo, CampoArea, CampoSelecao, Icone } from '@/shared/ui';
import {
  expressaoDasRegras,
  primeiroValor,
  regraNova,
  regrasDaExpressao,
  resumoDasRegras,
  type Operador,
  type Regra,
} from '../lib/condicao';
import { ComCamposDoEvento } from './inserir-campo';

export function CondicaoGuiada({
  rotulo,
  ajuda,
  campos,
  contexto,
  valor,
  aoMudar,
  vazia = 'Sem condição: vale para todo evento.',
}: {
  rotulo: string;
  /** O que a regra decide, numa frase. */
  ajuda: string;
  /** Os campos que se comparam, com os valores prontos. */
  campos: readonly CampoDoEvento[];
  /** Os nomes do evento para o "Inserir" do texto. */
  contexto: readonly string[];
  valor: string;
  aoMudar: (valor: string) => void;
  /** O que a linha vazia diz: no gatilho, sem condição vale tudo. */
  vazia?: string;
}): ReactNode {
  const id = useId();
  /** A pessoa pediu o texto. Sem pedido, o texto abre só para o que as escolhas não leem. */
  const [pediuTexto, definirPediuTexto] = useState(false);
  /*
   * As regras que este componente acabou de gravar, junto com a expressão que
   * elas deram. A regra recém-acrescentada sem valor ainda não vira texto, e
   * sumiria se a lista saísse só da expressão. Quando a expressão muda por fora
   * (descartar as alterações, a versão no ar, o catálogo que chegou depois), ela
   * deixa de ser a gravada aqui, e as regras voltam a ser lidas dela.
   */
  const [gravadas, definirGravadas] = useState<{
    expressao: string;
    regras: readonly Regra[];
  } | null>(null);
  const lidas = regrasDaExpressao(valor, campos);
  const minhas = gravadas !== null && gravadas.expressao === valor ? gravadas.regras : null;
  const regras = minhas ?? lidas ?? [];
  const escrevendo = campos.length === 0 || pediuTexto || (minhas === null && lidas === null);

  const mudarRegras = (novas: readonly Regra[]): void => {
    const expressao = expressaoDasRegras(novas, campos);
    definirGravadas({ expressao, regras: novas });
    aoMudar(expressao);
  };
  const mudarRegra = (i: number, mudanca: Partial<Regra>): void =>
    mudarRegras(regras.map((r, j) => (j === i ? { ...r, ...mudanca } : r)));

  // Quem digita fica no texto: a tela não troca para as escolhas no meio da frase.
  const escrever = (texto: string): void => {
    definirPediuTexto(true);
    aoMudar(texto);
  };

  if (escrevendo) {
    const podeVoltar = campos.length > 0 && lidas !== null;
    return (
      <div className="editor-do-mapa__condicao">
        <ComCamposDoEvento campos={contexto} valor={valor} aoMudar={escrever}>
          {(ref) => (
            <CampoArea
              ref={ref}
              rotulo={rotulo}
              ajuda={`${ajuda} Texto fixo vai entre aspas, como "contact".`}
              rows={3}
              spellCheck={false}
              value={valor}
              onChange={(e) => escrever(e.target.value)}
            />
          )}
        </ComCamposDoEvento>
        {campos.length > 0 &&
          (podeVoltar ? (
            <Botao
              variante="fantasma"
              miudo
              onClick={() => {
                definirGravadas(null);
                definirPediuTexto(false);
              }}
            >
              Montar com as opções
            </Botao>
          ) : (
            <Botao
              variante="fantasma"
              miudo
              onClick={() => {
                definirGravadas(null);
                definirPediuTexto(false);
                aoMudar('');
              }}
            >
              Apagar e montar com as opções
            </Botao>
          ))}
      </div>
    );
  }

  const nova = regraNova(campos, regras);
  return (
    <fieldset className="editor-do-mapa__condicao" aria-describedby={`${id}-resumo`}>
      <legend className="editor-do-mapa__condicao-titulo">{rotulo}</legend>
      <p className="editor-do-mapa__painel-ajuda">{ajuda}</p>
      {regras.map((r, i) => {
        const campo = campos.find((c) => c.caminho === r.caminho);
        return (
          <div key={i} className="editor-do-mapa__regra">
            {i > 0 && <span className="editor-do-mapa__regra-e">e</span>}
            <CampoSelecao
              rotulo="Campo"
              value={r.caminho}
              onChange={(e) => {
                const escolhido = campos.find((c) => c.caminho === e.target.value);
                if (escolhido) {
                  mudarRegra(i, { caminho: escolhido.caminho, valor: primeiroValor(escolhido) });
                }
              }}
            >
              {campos.map((c) => (
                <option key={c.caminho} value={c.caminho}>
                  {c.titulo}
                </option>
              ))}
            </CampoSelecao>
            <CampoSelecao
              rotulo="Comparação"
              value={r.operador}
              onChange={(e) => mudarRegra(i, { operador: e.target.value as Operador })}
            >
              <option value="igual">é</option>
              <option value="diferente">não é</option>
            </CampoSelecao>
            {campo?.tipo === 'sim_nao' ? (
              <CampoSelecao
                rotulo="Valor"
                value={r.valor}
                onChange={(e) => mudarRegra(i, { valor: e.target.value })}
              >
                <option value="true">Sim</option>
                <option value="false">Não</option>
              </CampoSelecao>
            ) : campo && campo.opcoes.length > 0 ? (
              <CampoSelecao
                rotulo="Valor"
                value={r.valor}
                onChange={(e) => mudarRegra(i, { valor: e.target.value })}
              >
                {!campo.opcoes.some((o) => o.valor === r.valor) && (
                  <option value={r.valor}>{r.valor || 'Escolha'}</option>
                )}
                {campo.opcoes.map((o) => (
                  <option key={o.valor} value={o.valor}>
                    {o.rotulo}
                  </option>
                ))}
              </CampoSelecao>
            ) : (
              <Campo
                rotulo="Valor"
                ajuda="Não há uma lista para escolher aqui: escreva o valor."
                spellCheck={false}
                value={r.valor}
                onChange={(e) => mudarRegra(i, { valor: e.target.value })}
              />
            )}
            <Botao
              variante="fantasma"
              icone
              miudo
              rotulo={`Tirar a regra ${i + 1}`}
              aria-label={`Tirar a regra ${i + 1}`}
              onClick={() => mudarRegras(regras.filter((_, j) => j !== i))}
            >
              <Icone nome="lixeira" />
            </Botao>
          </div>
        );
      })}
      <p className="editor-do-mapa__painel-ajuda" id={`${id}-resumo`}>
        {regras.length === 0 ? vazia : `Só quando ${resumoDasRegras(regras, campos)}.`}
      </p>
      <div className="editor-do-mapa__condicao-acoes">
        <Botao
          variante="secundario"
          miudo
          onClick={() => nova && mudarRegras([...regras, nova])}
          desabilitado={nova === null}
          motivo="Este evento não traz campos para comparar."
        >
          <Icone nome="mais" />
          {regras.length === 0 ? 'Adicionar condição' : 'Adicionar outra'}
        </Botao>
        <Botao variante="fantasma" miudo onClick={() => definirPediuTexto(true)}>
          Escrever a expressão
        </Botao>
      </div>
    </fieldset>
  );
}
